import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import MenuIcon from '@mui/icons-material/Menu';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import PhoneIcon from '@mui/icons-material/Phone';
import EmailIcon from '@mui/icons-material/Email';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import TwitterIcon from '@mui/icons-material/Twitter';
import FacebookIcon from '@mui/icons-material/Facebook';
import LinkedInIcon from '@mui/icons-material/LinkedIn';
import InstagramIcon from '@mui/icons-material/Instagram';
import { styled } from '@mui/material/styles';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import AppBar from '@mui/material/AppBar';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import IconButton from '@mui/material/IconButton';
import Drawer from '@mui/material/Drawer';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import { fetchPublicFeatureFlags } from '../services/featureFlagsApi';
import { getDevLoyaltyEnabledOverride } from '../utils/devOverrides';
import { loyaltySession } from '../utils/session';

// AppBar is solid and dark by default (avoid white/transparent flash at top of Home)
const StyledAppBar = styled(AppBar, {
  shouldForwardProp: (prop) => prop !== 'scrolled',
})(({ theme }) => ({
  background: 'rgba(15,17,18,0.96)',
  backdropFilter: 'saturate(140%) blur(6px)',
  boxShadow: '0 6px 24px rgba(0,0,0,0.32)',
  borderBottom: '1px solid rgba(255,255,255,0.06)',
  transition: 'background 240ms ease, backdrop-filter 240ms ease, border 240ms ease',
  zIndex: theme.zIndex.appBar,
}));

const StyledToolbar = styled(Toolbar)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'space-between',
  minHeight: '75px',
  height: '75px',
  padding: '0 24px',
  maxWidth: '1400px',
  width: '100%',
  margin: '0 auto',
  [theme.breakpoints.down('sm')]: {
    minHeight: '60px',
    height: '60px',
    padding: '0 16px',
  },
}));


const NavButton = styled(Button)(({ theme }) => ({
  color: 'white',
  // force nav links to use the CSS variable set from the theme so they absolutely match body text
  fontFamily: 'var(--app-font-family)',
  fontSize: '100%',
  lineHeight: 'inherit',
  // bold weight for navigation buttons
  fontWeight: 600,
  // enforce same sizing/spacing/antialiasing as body so rendering looks identical
  fontSize: '1.2rem',
  letterSpacing: '0px',
  WebkitFontSmoothing: 'antialiased',
  textRendering: 'optimizeLegibility',
  fontStyle: 'inherit',
  border: 0,
  outline: 0,
  msWordWrap: 'break-word',
  wordWrap: 'break-word',
  padding: '8px 16px',
  height: '40px',
  position: 'relative',
  transition: 'all 0.2s ease',
  textTransform: 'none',
  letterSpacing: '0.5px',
  opacity: 0.9,
  '&:hover': {
    backgroundColor: 'transparent',
    opacity: 1,
  },
  '&.active': {
    opacity: 1,
    fontWeight: 700,
  },
  [theme.breakpoints.down('lg')]: {
    padding: '6px 12px',
    fontSize: '1rem',
  },
}));

const PublicLayout = ({ children }) => {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);
  // Make the AppBar solid on all non-home pages so navigation remains visible.
  const [scrolled, setScrolled] = useState(location.pathname !== '/');
  const [languageSetFromUrl, setLanguageSetFromUrl] = useState(false);
  const [isLoyaltyEnabled, setIsLoyaltyEnabled] = useState(false);

  // Detect language from URL path only once on initial mount or when URL explicitly changes language
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const urlLang = pathSegments[0];

    // Only set language from URL if it's 'bg' or 'en' and we haven't already processed it
    if ((urlLang === 'bg' || urlLang === 'en') && !languageSetFromUrl) {
      const newLang = urlLang;
      const currentLang = i18n.language || localStorage.getItem('menuLanguage') || 'en';

      if (currentLang !== newLang) {
        localStorage.setItem('menuLanguage', newLang);
        i18n.changeLanguage(newLang);
      }
      setLanguageSetFromUrl(true);
    }
  }, [location.pathname, i18n, languageSetFromUrl]);

  // Initialize from localStorage on mount
  useEffect(() => {
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const urlLang = pathSegments[0];

    // Only use localStorage if URL doesn't specify language
    if (urlLang !== 'bg' && urlLang !== 'en') {
      // First, check if loyalty user has preferred language
      const loyaltyUser = loyaltySession.getUser();
      if (loyaltyUser) {
        if (loyaltyUser.preferred_language && i18n.language !== loyaltyUser.preferred_language) {
          i18n.changeLanguage(loyaltyUser.preferred_language);
          return;
        }
      }

      // Fallback to saved language or default
      const savedLanguage = localStorage.getItem('menuLanguage') || 'en';
      if (i18n.language !== savedLanguage) {
        i18n.changeLanguage(savedLanguage);
      }
    }
  }, []); // Only run once on mount

  // Fetch is_loyalty_enabled feature flag
  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const devOverride = getDevLoyaltyEnabledOverride();
        if (devOverride !== null) {
          setIsLoyaltyEnabled(devOverride);
          return;
        }

        const flags = await fetchPublicFeatureFlags();
        if (!mounted) return;
        const loyaltyFlag = flags.find(f => f.key === 'is_loyalty_enabled');
        setIsLoyaltyEnabled(loyaltyFlag?.value === true || loyaltyFlag?.enabled === true);
      } catch (err) {
        // Keep default false on error
        setIsLoyaltyEnabled(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  const allNavLinks = [
    { text: t('nav.home'), path: '/' },
    { text: t('nav.menu'), path: '/menu' },
    { text: t('nav.gallery'), path: '/gallery' },
    { text: t('nav.contact'), path: '/contact' },
    { text: t('nav.policy'), path: '/policy' },
    { text: t('nav.loyalty'), path: '/loyalty' }
  ];

  // Filter out loyalty link if feature flag is disabled
  const NavLinks = isLoyaltyEnabled
    ? allNavLinks
    : allNavLinks.filter(link => link.path !== '/loyalty');

  useEffect(() => {
    function onScroll() {
      // keep scrolled true if user navigated away from home
      setScrolled(window.scrollY > 24 || location.pathname !== '/');
    }
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // When route changes ensure the AppBar is solid on non-home pages
  useEffect(() => {
    try {
      setScrolled(window.scrollY > 24 || location.pathname !== '/');
    } catch (e) {
      setScrolled(location.pathname !== '/');
    }
  }, [location.pathname]);

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box onClick={handleDrawerToggle} sx={{ textAlign: 'center', mt: 2 }}>
      <List sx={{ px: 2 }}>
        {NavLinks.map((link) => (
          <ListItem key={link.path} disablePadding>
            <ListItemButton
              component={RouterLink}
              to={link.path}
              sx={{
                textAlign: 'center',
                margin: '8px 0',
                borderRadius: '8px',
                backgroundColor: location.pathname === link.path ? 'rgba(255, 107, 0, 0.1)' : 'transparent',
                transition: 'all 0.3s ease',
                '&:hover': {
                  backgroundColor: 'rgba(255, 107, 0, 0.15)',
                  transform: 'translateX(8px)',
                },
              }}
            >
              <ListItemText
                primary={link.text}
                primaryTypographyProps={{ sx: { fontFamily: 'var(--app-font-family)', fontSize: '1.0625rem', fontWeight: 400, letterSpacing: '0px', WebkitFontSmoothing: 'antialiased', textRendering: 'optimizeLegibility' } }}
                sx={{
                  color: location.pathname === link.path ? '#ff6b00' : 'inherit',
                }}
              />
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
      <StyledAppBar position="fixed" scrolled={scrolled}>
        <StyledToolbar>
          <RouterLink to="/" style={{ textDecoration: 'none', color: 'white' }}>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography
                variant={isMobile ? "h6" : "h6"}
                component="div"
                sx={{
                  fontFamily: 'Yeseva One, cursive',
                  fontWeight: 400,
                  fontStyle: 'normal',
                  lineHeight: '1.25em',
                  textDecoration: 'none',
                  textTransform: 'none',
                  letterSpacing: '0px',
                  color: 'white',
                  opacity: 1,
                  transition: 'opacity 0.2s ease',
                  fontSize: { xs: '2.6rem', sm: '2.2rem', md: '2em' },
                  '&:hover': {
                    opacity: 1
                  }
                }}
              >
                Shisha Vibes
              </Typography>
            </Box>
          </RouterLink>
          <Stack direction="row" spacing={3} alignItems="center">
            {!isMobile && (
              <>
                {NavLinks.map((link) => (
                  <NavButton
                    key={link.path}
                    component={RouterLink}
                    to={link.path}
                    color={location.pathname === link.path ? 'primary' : 'inherit'}
                  >
                    {link.text}
                  </NavButton>
                ))}
              </>
            )}
            {isMobile && (
              <button
                className="navbar-toggle"
                onClick={handleDrawerToggle}
                aria-expanded={mobileOpen}
                aria-label="Toggle navigation menu"
                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '0.5rem', marginLeft: '8px', display: 'flex', alignItems: 'center' }}
              >
                <MenuIcon style={{ fontSize: 32, color: '#fff' }} />
              </button>
            )}
          </Stack>
        </StyledToolbar>
      </StyledAppBar>

      <Box component="nav">
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true, // Better mobile performance
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: 260,
              background: '#151515',
              color: 'white',
              borderLeft: '1px solid rgba(255, 255, 255, 0.1)',
            },
          }}
        >
          {drawer}
        </Drawer>
      </Box>
      {/* Ensure main content is pushed below the fixed AppBar so buttons (eg. Translations) are not hidden under it */}
      <Box component="main" sx={{ flexGrow: 1, pt: { xs: '60px', md: '75px' } }}>
        {children}
      </Box>
      <Box component="footer" sx={{ mt: 'auto', bgcolor: '#060708', color: '#cfcfcf', py: { xs: 6, md: 10 } }}>
        <Box sx={{ maxWidth: 1200, mx: 'auto', px: 3 }}>
          <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 4, alignItems: { xs: 'center', md: 'flex-start' }, justifyContent: 'center' }}>
            {/* Left: Contacts - Hide on contact page */}
            {location.pathname !== '/contact' && (
              <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 320px' }, color: '#cfcfcf', textAlign: { xs: 'center', md: 'left' } }}>
                <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 700 }}>{t('footer.contacts')}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, alignItems: { xs: 'center', md: 'flex-start' } }}>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <LocationOnIcon sx={{ color: '#c7a37f' }} />
                    {/* Clickable address - opens Google Maps directions in a new tab */}
                    <a
                      href={'https://goo.gl/maps/n9RFJXRWtt5PyLDW9'}
                      target="_blank"
                      rel="noopener noreferrer"
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      aria-label="Open address in Google Maps"
                    >
                      <Typography variant="body2" sx={{ color: '#bdbdbd' }}>{t('footer.address')}</Typography>
                    </a>
                  </Box>
                  <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                    <PhoneIcon sx={{ color: '#c7a37f' }} />
                    {/* Clickable phone link: tel: will open the dialer on mobile devices */}
                    <a
                      href={'tel:' + '+359878101363'}
                      style={{ textDecoration: 'none', color: 'inherit' }}
                      aria-label="Call +359 878 101 363"
                    >
                      <Typography variant="body2" sx={{ color: '#bdbdbd' }}>{t('footer.phone')}</Typography>
                    </a>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Center: Logo + quote + social */}
            <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 420px' }, textAlign: 'center', px: 2, py: { xs: 3, md: 0 } }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                <Box sx={{ display: 'flex', gap: 1.25, mt: 2, justifyContent: 'center' }}>
                  {[
                    { Icon: FacebookIcon, href: 'https://www.facebook.com/p/Shisha-Vibes-100052568361094', target: '_blank' },
                    { Icon: InstagramIcon, href: 'https://www.instagram.com/shishavibesbg/', target: '_blank' },
                  ].map((s, i) => (
                    <IconButton
                      key={i}
                      component="a"
                      href={s.href}
                      target={s.target || '_blank'}
                      rel="noopener noreferrer"
                      size="small"
                      sx={{
                        color: '#ddd',
                        border: '1px solid rgba(255,255,255,0.06)',
                        width: { xs: 40, md: 44 },
                        height: { xs: 40, md: 44 },
                      }}
                    >
                      <s.Icon sx={{ fontSize: { xs: 18, md: 20 } }} />
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Box>

            {/* Right: Working Hours (centered) - Hide on contact page */}
            {location.pathname !== '/contact' && (
              <Box sx={{ flex: { xs: '1 1 100%', md: '0 0 280px' }, textAlign: 'center', display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <Typography variant="h6" sx={{ color: '#fff', mb: 2, fontWeight: 700, textAlign: 'center' }}>{t('footer.workingHours')}</Typography>
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, alignItems: 'center' }}>
                  <Typography variant="body2" sx={{ color: '#bdbdbd' }}>{t('footer.schedule')}</Typography>
                  <Typography variant="body2" sx={{ color: '#d5b08a' }}>{t('footer.hours')}</Typography>
                </Box>
              </Box>
            )}
          </Box>

          <Box sx={{ borderTop: '1px solid rgba(255,255,255,0.03)', mt: 6, pt: 4 }}>
            <Typography variant="body2" align="center" sx={{ color: '#6f6f70' }}>{t('footer.copyright', { year: new Date().getFullYear() })}</Typography>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default PublicLayout;